import { ref } from 'vue'
import { defineStore } from 'pinia'
import {
  collection,
  doc,
  setDoc,
  onSnapshot,
  serverTimestamp,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from '@/firebase'
import { useSyncStore } from './sync'
import { writeAuditLog } from './auditLog'
import type { FormData } from '@/types'

export const useFormsStore = defineStore('forms', () => {
  const syncStore = useSyncStore()

  // Map of formType → FormData
  const forms = ref<Record<string, FormData>>({})
  const loading = ref(false)

  let _unsubscribe: Unsubscribe | null = null
  let _subscribedIncidentId: string | null = null

  function subscribeToForms(incidentId: string): void {
    // Deduplicate — don't re-subscribe for the same incident
    if (_subscribedIncidentId === incidentId) return

    _unsubscribe?.()
    _subscribedIncidentId = incidentId
    loading.value = true

    const formsCol = collection(db, 'incidents', incidentId, 'forms')
    _unsubscribe = onSnapshot(formsCol, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'removed') {
          delete forms.value[change.doc.id]
        } else {
          forms.value[change.doc.id] = { form_id: change.doc.id, ...change.doc.data() } as FormData
        }
      })
      loading.value = false
    })
  }

  async function saveFormField(
    incidentId: string,
    formType: string,
    fieldPath: string,
    value: unknown,
  ): Promise<void> {
    const oldValue = getFormData(formType)[fieldPath]

    const formRef = doc(db, 'incidents', incidentId, 'forms', formType)

    syncStore.pendingWrites++
    try {
      await setDoc(
        formRef,
        {
          form_type: formType,
          incident_id: incidentId,
          data: { [fieldPath]: value },
          last_updated_at: serverTimestamp(),
        },
        { merge: true },
      )
      writeAuditLog(
        incidentId,
        'form.updated',
        'form',
        formType,
        { field: fieldPath, before: oldValue ?? null },
        { field: fieldPath, after: value ?? null },
      )
    } finally {
      syncStore.pendingWrites--
    }
  }

  function getFormData(formType: string): Record<string, unknown> {
    return (forms.value[formType]?.data as Record<string, unknown>) ?? {}
  }

  return { forms, loading, subscribeToForms, saveFormField, getFormData }
})
